import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react'

/*
 * The handle of a tray attached to a screen edge (owner, 2026-10-16: "a little handle area on the
 * left tray; tap to collapse, drag to move. It gets in the way of drawing"). A small tab on the
 * tray's open side: a tap slides the tray off past its edge, leaving only the tab; another tap
 * brings it back. A drag moves the tray up or down along its edge (kept on screen). Both are
 * remembered for that edge, across pages and visits.
 *
 * The offset is the CSS `translate` property, so it adds to whatever `transform` the page layout
 * gives the tray (a tray scaled down to fit).
 */

const TAB_W = 22
const TAB_H = 52
/** A press that moves less than this is a tap. */
const DRAG_SLOP_PX = 6
const MARGIN_PX = 8
const SLIDE = 'translate 220ms ease-out'

type Edge = 'left' | 'right'
type TrayState = { collapsed: boolean; dy: number }

const storageKey = (edge: Edge) => `huebo:tray-handle:${edge}`

function readState(edge: Edge): TrayState {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(storageKey(edge)) ?? 'null')
    if (parsed && typeof parsed === 'object') {
      const { collapsed, dy } = parsed as Partial<TrayState>
      return { collapsed: collapsed === true, dy: typeof dy === 'number' && Number.isFinite(dy) ? dy : 0 }
    }
  } catch { /* storage unavailable or not JSON */ }
  return { collapsed: false, dy: 0 }
}

function writeState(edge: Edge, state: TrayState): void {
  try { localStorage.setItem(storageKey(edge), JSON.stringify(state)) } catch { /* storage unavailable */ }
}

/**
 * The tray's offset (`style`, spread into the tray's root) and its tab (`handle`, rendered inside
 * the root). `rootRef` is the tray's root element (measured for the slide and the clamp).
 */
export function useTrayHandle(edge: Edge | undefined, rootRef: RefObject<HTMLElement | null>, ink: string): { style: CSSProperties; handle: ReactNode } {
  const [state, setState] = useState<TrayState>(() => (edge ? readState(edge) : { collapsed: false, dy: 0 }))
  const [width, setWidth] = useState(0)
  const [dragging, setDragging] = useState(false)
  const press = useRef<{ id: number; y0: number; dy0: number; moved: boolean; min: number; max: number } | null>(null)

  // The tray's width, for sliding it out of sight (it changes with the page's buttons), and a
  // remembered offset kept on screen: another page's tray is taller, a phone turns.
  useEffect(() => {
    const el = rootRef.current
    if (!edge || !el) return
    const measure = () => {
      setWidth(el.offsetWidth)
      if (press.current) return
      const rect = el.getBoundingClientRect()
      const vh = window.visualViewport?.height ?? window.innerHeight
      const push = rect.top < MARGIN_PX ? MARGIN_PX - rect.top : rect.bottom > vh - MARGIN_PX ? (vh - MARGIN_PX) - rect.bottom : 0
      if (push !== 0 && rect.height < vh - 2 * MARGIN_PX) setState((st) => ({ ...st, dy: st.dy + push }))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    return () => { ro.disconnect(); window.removeEventListener('resize', measure) }
  }, [edge, rootRef])

  const commit = useCallback((next: TrayState) => {
    setState(next)
    if (edge) writeState(edge, next)
  }, [edge])

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    const el = rootRef.current
    if (!el) return
    // How far the tray may move: its whole box stays on screen (the offset already applied counted).
    const rect = el.getBoundingClientRect()
    const vh = window.visualViewport?.height ?? window.innerHeight
    press.current = { id: e.pointerId, y0: e.clientY, dy0: state.dy, moved: false, min: state.dy - (rect.top - MARGIN_PX), max: state.dy + (vh - MARGIN_PX - rect.bottom) }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    const p = press.current
    if (!p || p.id !== e.pointerId) return
    const travel = e.clientY - p.y0
    if (!p.moved && Math.abs(travel) < DRAG_SLOP_PX) return
    if (!p.moved) { p.moved = true; setDragging(true) }
    setState((s) => ({ ...s, dy: Math.min(p.max, Math.max(p.min, p.dy0 + travel)) }))
  }
  const onPointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    const p = press.current
    if (!p || p.id !== e.pointerId) return
    press.current = null
    setDragging(false)
    if (p.moved) setState((s) => { if (edge) writeState(edge, s); return s })
    else commit({ ...state, collapsed: !state.collapsed })
  }
  const onPointerCancel = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (press.current?.id !== e.pointerId) return
    press.current = null
    setDragging(false)
  }

  if (!edge) return { style: {}, handle: null }

  const away = state.collapsed ? (edge === 'left' ? -width : width) : 0
  const style: CSSProperties = {
    translate: `${away}px ${state.dy}px`,
    transition: dragging ? undefined : SLIDE,
    // Out of sight but for the tab: its buttons take no presses.
    ...(state.collapsed ? { pointerEvents: 'none' } : null),
  }
  const handle = (
    <button
      type="button"
      aria-label={state.collapsed ? 'Show the tray' : 'Hide the tray (drag to move it)'}
      aria-expanded={!state.collapsed}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: '50%',
        [edge === 'left' ? 'right' : 'left']: -TAB_W + 2,
        width: TAB_W,
        height: TAB_H,
        marginTop: -TAB_H / 2,
        padding: 0,
        border: 'none',
        borderRadius: edge === 'left' ? '0 14px 14px 0' : '14px 0 0 14px',
        background: 'inherit',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: edge === 'left' ? '4px 4px 12px rgba(20, 30, 40, 0.10)' : '-4px 4px 12px rgba(20, 30, 40, 0.10)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        cursor: dragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        pointerEvents: 'auto',
      }}
    >
      {/* Two grip lines: something to hold. */}
      <span aria-hidden style={{ width: 2.5, height: 18, borderRadius: 2, background: ink, opacity: 0.35 }} />
      <span aria-hidden style={{ width: 2.5, height: 18, borderRadius: 2, background: ink, opacity: 0.35 }} />
    </button>
  )
  return { style, handle }
}
